import type { SymmetricKey } from '@faceup/crypto'
import { areArraysEqual } from '@faceup/utils'
import { type Dispatch, type SetStateAction, useEffect, useMemo, useState } from 'react'
import { type FragmentType, getFragmentData, graphql } from '../__generated__'
import { FormItemType } from '../__generated__/graphql'
import {
  type AnswerForMutation,
  usePrepareFormItemAnswersForMutation,
} from './usePrepareFormItemAnswersForMutation'

const fragments = {
  useManageReportCustomInputsFragments_answer: graphql(`
    fragment useManageReportCustomInputsFragments_answer on FormItemAnswer {
      id
      values
      attachments {
        id
        name
      }
      formItem {
        id
        formItemId
        type
      }

      ...usePrepareFormItemAnswersForMutationFragments_answer
    }
  `),
}

export type Answers = Record<string, Answer>

/** An attachment already stored on the case, as opposed to a freshly picked `File`. */
export type UploadedAttachment = {
  id: string
  name: string
}

export type Answer = (
  | {
      type: 'text'
      values: string[] | null
    }
  | {
      type: 'file'
      /** Files newly picked in this form, not uploaded yet. */
      values: File[] | null
      /** Already uploaded attachments the user still wants to keep. */
      attachments?: UploadedAttachment[]
    }
) & {
  error?: string
}

const getAttachmentIds = (answer: Answer | undefined) =>
  answer?.type === 'file' ? (answer.attachments ?? []).map(({ id }) => id) : []

export const useManageReportCustomInputs = (
  categoryId: string | null,
  reportSourceId: string | null,
  answers?: FragmentType<typeof fragments.useManageReportCustomInputsFragments_answer>[]
): {
  customFormItems: Answers
  setCustomFormItems: Dispatch<SetStateAction<Answers>>
  resetCustomFormItems: () => void
  validateCustomFormItems: () => Promise<boolean>
  getValuesForMutation: (reportKey?: SymmetricKey) => Promise<AnswerForMutation[]>
  areCustomFormItemsInDefaultState: () => boolean
} => {
  const { prepareForm } = usePrepareFormItemAnswersForMutation()
  const defaultFormItems = useMemo(() => parseGqlAnswerToAnswersType(answers ?? []), [answers])

  const [customFormItems, setCustomFormItems] = useState<Answers>({})

  useEffect(() => {
    setCustomFormItems(defaultFormItems)
  }, [defaultFormItems])

  const { validateAnswers, getValuesForMutation: prepareValuesForMutation } = prepareForm(
    // this shouldn't ever happen
    reportSourceId ?? '',
    categoryId ?? ''
  )

  const resetCustomFormItems = () => {
    setCustomFormItems({})
  }

  const validateCustomFormItems = async () => validateAnswers(customFormItems, setCustomFormItems)

  const isValueInDefaultState = (formItemId: string, values: string[] | null) => {
    const defaultFormItem = defaultFormItems[formItemId]
    return defaultFormItem?.values && values && areArraysEqual(defaultFormItem?.values, values)
  }

  /**
   * Files the case already had but that the user removed while editing. The form
   * only tracks what should be kept, so the difference has to be derived against
   * the answers we loaded.
   */
  const getFileIdsToDelete = (formItemId: string) => {
    const currentAnswer = customFormItems[formItemId]
    if (!currentAnswer) {
      // never delete based on an answer the form doesn't know about
      return []
    }
    const keptIds = getAttachmentIds(currentAnswer)
    return getAttachmentIds(defaultFormItems[formItemId]).filter(id => !keptIds.includes(id))
  }

  const getValuesForMutation = async (reportKey?: SymmetricKey) =>
    (await prepareValuesForMutation(customFormItems, reportKey))
      .filter(({ values, formItemId }) => !isValueInDefaultState(formItemId, values))
      .map(answer => {
        const filesToDelete = getFileIdsToDelete(answer.formItemId)
        return filesToDelete.length > 0 ? { ...answer, filesToDelete } : answer
      })

  const areCustomFormItemsInDefaultState = () =>
    isObjectContained(defaultFormItems, customFormItems) &&
    isObjectContained(customFormItems, defaultFormItems)

  return {
    customFormItems,
    setCustomFormItems,
    resetCustomFormItems,
    validateCustomFormItems,
    getValuesForMutation,
    areCustomFormItemsInDefaultState,
  }
}

export const parseGqlAnswerToAnswersType = (
  answers: readonly FragmentType<typeof fragments.useManageReportCustomInputsFragments_answer>[]
): Answers =>
  answers.reduce<Answers>((result, _answer) => {
    const answer = getFragmentData(fragments.useManageReportCustomInputsFragments_answer, _answer)
    let formItemData: Answer = { type: 'text', values: answer.values ?? '' }
    if (answer.formItem.type === FormItemType.File) {
      formItemData = {
        type: 'file',
        values: [] as File[],
        attachments: answer.attachments.map(({ id, name }) => ({ id, name })),
      }
    }
    return {
      ...result,
      [answer.formItem.formItemId]: formItemData,
    }
  }, {}) ?? {}

const isObjectContained = (obj1: Answers, obj2: Answers): boolean =>
  Object.keys(obj1).every(
    key =>
      areArraysEqual(obj1[key]?.values ?? [], obj2[key]?.values ?? []) &&
      // File answers keep their content in `attachments`, so comparing `values`
      // alone would report "no changes" after removing an existing attachment
      areArraysEqual(getAttachmentIds(obj1[key]), getAttachmentIds(obj2[key]))
  )
